export const KITCHEN_ROUTING_CACHE_SECTION = "kitchen-routing";

export type KitchenRoutingEntry = {
  variantId: string;
  stationId: string;
  stationName: string;
  stationCode?: string | null;
};

/**
 * Default station used for items that have no routing rule.
 * Mirrors the previous single "Local Kitchen" ticket so the kitchen
 * never loses sight of an unrouted item when working offline.
 */
export const DEFAULT_LOCAL_STATION = {
  stationId: "LOCAL_UNASSIGNED",
  stationName: "Kitchen",
  stationCode: "LOCAL_UNASSIGNED" as string | null,
};

export type KitchenStationGroup<TItem> = {
  stationId: string;
  stationName: string;
  stationCode: string | null;
  items: TItem[];
};

/**
 * Groups items into one bucket per kitchen station using the branch
 * routing rules. Items without a routing rule fall back to the default
 * "Kitchen" station. Insertion order is preserved so ticket ordering is
 * stable across reprints.
 */
export function groupItemsByStation<TItem extends { variantId?: string | null }>(
  items: TItem[],
  routingEntries: KitchenRoutingEntry[],
): Array<KitchenStationGroup<TItem>> {
  const routingMap = new Map(
    routingEntries.map((entry) => [entry.variantId, entry]),
  );
  const groups = new Map<string, KitchenStationGroup<TItem>>();
  const order: string[] = [];

  for (const item of items) {
    const routing = item.variantId ? routingMap.get(item.variantId) : undefined;
    const stationId = routing?.stationId ?? DEFAULT_LOCAL_STATION.stationId;
    const stationName = routing?.stationName ?? DEFAULT_LOCAL_STATION.stationName;
    const stationCode =
      routing?.stationCode ?? DEFAULT_LOCAL_STATION.stationCode;

    let group = groups.get(stationId);
    if (!group) {
      group = { stationId, stationName, stationCode, items: [] };
      groups.set(stationId, group);
      order.push(stationId);
    }
    group.items.push(item);
  }

  return order.map((stationId) => {
    const group = groups.get(stationId);
    if (!group) {
      throw new Error(`Missing station group for ${stationId}`);
    }
    return group;
  });
}
